import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { getQueryFn } from '@/lib/queryClient';
import { useAuth0 } from '@auth0/auth0-react';

interface AuthRouteGuardProps {
  allowedPaths?: string[]; // simple wildcard patterns like '/public/*', '/login'
}

function patternToRegex(pattern: string): RegExp {
  // Escape regex special chars except * then convert * to .*
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp('^' + escaped + '$');
}

export default function AuthRouteGuard({ allowedPaths = [] }: AuthRouteGuardProps) {
  const [location, setLocation] = useLocation();
  const { loginWithRedirect, isLoading: isAuth0Loading, isAuthenticated } = useAuth0();

  const allowedRegexes = useMemo(() => {
    const defaults = ['/login'];
    return [...defaults, ...allowedPaths].map(patternToRegex);
  }, [allowedPaths]);

  const isAllowedPublic = useMemo(() => {
    return allowedRegexes.some((r) => r.test(location));
  }, [allowedRegexes, location]);

  // Token-gated public access: if a `t` token is present in the URL,
  // allow access to avoid triggering login during public flows.
  const hasPublicToken = useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      const params = new URLSearchParams(window.location.search);
      if (params.has('t')) {
        // Cache token for subsequent navigations where querystring is preserved but we want extra safety
        try { sessionStorage.setItem('public_token', params.get('t') || ''); } catch {}
        return true;
      }
      // Fallback to cached token during fast route transitions
      const cached = sessionStorage.getItem('public_token');
      return Boolean(cached);
    } catch {
      return false;
    }
  }, [location]);

  const isTokenPublicRoute = hasPublicToken && location.startsWith('/value-assessment/');

  const { data: authResponse, isLoading } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: getQueryFn({ on401: 'returnNull' }),
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: !isAllowedPublic && !isTokenPublicRoute,
  });

  const user = authResponse?.user;

  useEffect(() => {
    if (isLoading || isAuth0Loading) return;

    if (!user && !isAllowedPublic && !isAuthenticated && !isTokenPublicRoute) {
      try {
        // Store intended path so login can redirect back
        sessionStorage.setItem('postLoginRedirect', location);
      } catch {}
      // Kick off Auth0 Universal Login directly
      loginWithRedirect();
    }
  }, [isLoading, isAuth0Loading, user, isAllowedPublic, isAuthenticated, isTokenPublicRoute, loginWithRedirect, location]);

  // This guard is a side-effect component; render nothing.
  return null;
}
